import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ReplayIcon from "@mui/icons-material/Replay";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";
import SourceIcon from "@mui/icons-material/Source";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  LinearProgress,
  TableSortLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  fetchTicketEvents,
  fetchTicketJobDetail,
  fetchTicketJobs,
  rerunTicketUploadJob,
  resetCreateForm,
  selectTicketJobs,
  selectTicketUploadDetailState,
  selectTicketUploadRerunStatus,
  selectTicketUploadsCreateStatus,
  selectTicketUploadsError,
  selectTicketUploadsEvents,
  selectTicketUploadsFilters,
  selectTicketUploadsLoading,
  selectTicketUploadsMeta,
  selectTicketUploadsPagination,
  selectTicketUploadsSort,
  setDateRangeFilter,
  setEventFilter,
  setStatusFilter,
  setTicketPage,
  setTicketPageSize,
  setTicketUploadsSort,
} from "../store/slices/ticketUploadsSlice";
import { selectEventItems } from "../store/slices/eventsSlice";
import { selectAccessToken } from "../store/slices/authSlice";
import { forceLogoutToLogin, isUnauthorizedApiResponse } from "../utils/unauthorized";

const statusOptions = ["ALL", "PENDING", "PROCESSING", "DONE", "ERROR"];
const statusColors = {
  DONE: "success",
  PROCESSING: "warning",
  ERROR: "error",
  PENDING: "default",
};
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const TicketUploadListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const filters = useSelector(selectTicketUploadsFilters);
  const pagination = useSelector(selectTicketUploadsPagination);
  const jobs = useSelector(selectTicketJobs);
  const meta = useSelector(selectTicketUploadsMeta);
  const loading = useSelector(selectTicketUploadsLoading);
  const error = useSelector(selectTicketUploadsError);
  const events = useSelector(selectTicketUploadsEvents);
  const fallbackEvents = useSelector(selectEventItems);
  const createStatus = useSelector(selectTicketUploadsCreateStatus);
  const rerunStatus = useSelector(selectTicketUploadRerunStatus);
  const detailState = useSelector(selectTicketUploadDetailState);
  const accessToken = useSelector(selectAccessToken);
  const sort = useSelector(selectTicketUploadsSort);

  const [dense, setDense] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [confirmRerunId, setConfirmRerunId] = useState(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const dropdownEvents = useMemo(() => {
    const source =
      Array.isArray(events) && events.length
        ? events
        : Array.isArray(fallbackEvents)
        ? fallbackEvents
        : [];
    return source.map((event) => ({ id: event.id, name: event.name }));
  }, [events, fallbackEvents]);

  const progressInfo = (job) => {
    if (!job.total_record) return { value: 0, label: "0%" };
    const completed = job.valid_record + job.failed_record;
    const value = Math.min(
      100,
      Math.round((completed / job.total_record) * 100)
    );
    return { value, label: `${value}%` };
  };

  const sortableColumns = {
    id: "id",
    status: "status",
    total: "total_record",
    valid: "valid_record",
    failed: "failed_record",
    created: "created_datetime",
    lastProcessed: "last_processed_datetime",
  };

  const handleSort = (fieldKey) => {
    const field = sortableColumns[fieldKey];
    if (!field) return;
    const isSame = sort.field === field;
    const nextDirection =
      isSame && sort.direction === "desc" ? "asc" : "desc";
    dispatch(setTicketUploadsSort({ field, direction: nextDirection }));
  };

  const renderHeader = (label, key) => {
    const field = sortableColumns[key];
    if (!field) return label;
    const isActive = sort.field === field;
    return (
      <TableSortLabel
        active={isActive}
        direction={isActive ? sort.direction : "desc"}
        onClick={() => handleSort(key)}
        sx={{
          color: "inherit",
          "& .MuiTableSortLabel-icon": {
            color: "inherit !important",
          },
        }}
      >
        {label}
      </TableSortLabel>
    );
  };
  const handleCreate = () => {
    dispatch(resetCreateForm());
    navigate("/ticket-uploads/new");
  };

  const handleOpenDatePicker = (ref) => () => {
    const input = ref.current;
    if (!input) return;
    const wasReadOnly = input.readOnly;
    input.readOnly = false;
    if (input.showPicker) {
      try {
        input.showPicker();
      } catch {
        input.focus();
      }
    } else {
      input.focus();
    }
    input.readOnly = wasReadOnly;
  };

  useEffect(() => {
    dispatch(fetchTicketJobs());
  }, [
    dispatch,
    filters.status,
    filters.eventId,
    filters.startDate,
    filters.endDate,
    sort.field,
    sort.direction,
    pagination.page,
    pagination.pageSize,
  ]);

  useEffect(() => {
    const hasPending = jobs.some(
      (job) => job.status === "PENDING" || job.status === "PROCESSING"
    );
    if (!hasPending) return;

    const interval = setInterval(() => {
      dispatch(fetchTicketJobs());
    }, 5000);

    return () => clearInterval(interval);
  }, [dispatch, jobs]);

  useEffect(() => {
    dispatch(fetchTicketEvents());
  }, [dispatch]);

  useEffect(() => {
    if (selectedJobId) {
      dispatch(fetchTicketJobDetail(selectedJobId));
    }
  }, [dispatch, selectedJobId]);

  const activeJob =
    detailState.job?.id === selectedJobId
      ? detailState.job
      : jobs.find((job) => job.id === selectedJobId);

  const handleDownload = async (jobId, path) => {
    setDownloadError(null);
    try {
      const response = await fetch(path, {
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });
      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (isUnauthorizedApiResponse(response, payload)) {
          forceLogoutToLogin();
          return;
        }
        throw new Error(payload?.resultDesc || "Download failed.");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", jobId);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err.message);
    }
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="›"
            sx={{ fontSize: 12, mb: 1, color: "text.secondary" }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Ticket Upload
            </Link>
            <Typography color="text.primary">Jobs</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Ticket Upload Jobs
          </Typography>
          <Typography color="text.secondary">
            Monitor batch uploads and re-run failed jobs when needed.
          </Typography>
        </Box>
        <Button
          size="large"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleCreate}
          sx={{
            alignSelf: { xs: "stretch", md: "center" },
            width: { xs: "100%", sm: "auto" },
            maxWidth: { xs: "calc(100vw - 32px)", sm: 220 },
            boxSizing: "border-box",
          }}
        >
          New Upload
        </Button>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: "0px 24px 48px rgba(15, 23, 42, 0.05)",
        }}
      >
        {(createStatus === "loading" || loading) && (
          <LinearProgress sx={{ mb: 2 }} />
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {downloadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {downloadError}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {statusOptions.map((status) => (
              <Chip
                key={status}
                label={status}
                color={filters.status === status ? "primary" : "default"}
                variant={filters.status === status ? "filled" : "outlined"}
                onClick={() => dispatch(setStatusFilter(status))}
              />
            ))}
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
          >
            <TextField
              select
              label="Event"
              fullWidth
              value={filters.eventId}
              onChange={(event) => dispatch(setEventFilter(event.target.value))}
              sx={{ minWidth: { md: 220 } }}
            >
              <MenuItem value="">All events</MenuItem>
              {dropdownEvents.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Start date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.startDate || ""}
              onChange={(event) =>
                dispatch(
                  setDateRangeFilter({
                    startDate: event.target.value || null,
                    endDate: filters.endDate,
                  })
                )
              }
              InputProps={{ readOnly: true }}
              inputProps={{ style: { cursor: "pointer" } }}
              sx={{ minWidth: { md: 220 } }}
              inputRef={startDateRef}
              onClick={handleOpenDatePicker(startDateRef)}
            />
            <TextField
              label="End date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.endDate || ""}
              onChange={(event) =>
                dispatch(
                  setDateRangeFilter({
                    startDate: filters.startDate,
                    endDate: event.target.value || null,
                  })
                )
              }
              InputProps={{ readOnly: true }}
              inputProps={{ style: { cursor: "pointer" } }}
              sx={{ minWidth: { md: 220 } }}
              inputRef={endDateRef}
              onClick={handleOpenDatePicker(endDateRef)}
            />
          </Stack>
        </Stack>

        <TableContainer>
          <Table size={dense ? "small" : "medium"} sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <TableHead
                sx={{
                  background: "linear-gradient(90deg, #030712 0%, #0f172a 50%, #111827 100%)",
                  "& th": {
                    borderBottom: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    color: theme.palette.common.white,
                    "& .MuiTableSortLabel-root": {
                      color: "inherit",
                      "&:hover": {
                        color: theme.palette.common.white,
                      },
                      "&.Mui-active": {
                        color: theme.palette.common.white,
                      },
                    },
                    "& .MuiTableSortLabel-icon": {
                      color: `${theme.palette.common.white} !important`,
                    },
                  },
                  "& th:first-of-type": {
                    borderTopLeftRadius: 10,
                  },
                  "& th:last-of-type": {
                    borderTopRightRadius: 10,
                  },
                }}
              >
              <TableRow>
                  <TableCell>{renderHeader("ID", "id")}</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>{renderHeader("Status", "status")}</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>{renderHeader("Total", "total")}</TableCell>
                  <TableCell>{renderHeader("Valid", "valid")}</TableCell>
                  <TableCell>{renderHeader("Failed", "failed")}</TableCell>
                  <TableCell>{renderHeader("Created", "created")}</TableCell>
                  <TableCell>
                    {renderHeader("Last Processed", "lastProcessed")}
                  </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const progress = progressInfo(job);
                return (
                  <TableRow key={job.id} hover>
                    <TableCell>#{job.id}</TableCell>
                    <TableCell>{job.event_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={job.status}
                        color={statusColors[job.status] || "default"}
                        size="small"
                        icon={
                          job.status === "PROCESSING" || job.status === "PENDING" ? (
                            <CircularProgress color="inherit" size={12} />
                          ) : undefined
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1}>
                        <LinearProgress
                          variant="determinate"
                          value={progress.value}
                          sx={{
                            height: 8,
                            borderRadius: 5,
                            backgroundColor:
                              progress.value >= 100
                                ? "success.light"
                                : "grey.200",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                progress.value >= 100
                                  ? "success.main"
                                  : "action.disabled",
                            },
                          }}
                        />
                        <Typography variant="caption">
                          {progress.label}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{job.total_record}</TableCell>
                    <TableCell>{job.valid_record}</TableCell>
                    <TableCell>{job.failed_record}</TableCell>
                    <TableCell>
                      {new Date(job.created_datetime).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {job.last_processed_datetime
                        ? new Date(
                            job.last_processed_datetime
                          ).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedJobId(job.id)}
                        >
                          <RemoveRedEyeIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Re-run job">
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              progress.value >= 100 ||
                              rerunStatus[job.id] === "loading"
                            }
                            onClick={() => setConfirmRerunId(job.id)}
                          >
                            <ReplayIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download source file">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!job.uploaded_file_path}
                            onClick={() =>
                              handleDownload(
                                `ticket-upload-${job.id}-source.xlsx`,
                                `${API_BASE_URL}/ticket-upload-jobs/${job.id}/source-file`
                              )
                            }
                          >
                            <SourceIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download error file">
                        <span>
                          <IconButton
                            size="small"
                            disabled={job.failed_record <= 0}
                            onClick={() =>
                              handleDownload(
                                `ticket-upload-${job.id}-errors.xlsx`,
                                `${API_BASE_URL}/ticket-upload-jobs/${job.id}/error-file`
                              )
                            }
                          >
                            <SimCardDownloadIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!jobs.length && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography align="center" color="text.secondary">
                      No upload jobs match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={dense}
                onChange={(event) => setDense(event.target.checked)}
              />
            }
            label="Dense"
          />
          <TablePagination
            component="div"
            count={meta.totalItems}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            page={pagination.page}
            onPageChange={(_, newPage) => dispatch(setTicketPage(newPage))}
            onRowsPerPageChange={(event) =>
              dispatch(setTicketPageSize(parseInt(event.target.value, 10)))
            }
          />
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(selectedJobId)}
        onClose={() => setSelectedJobId(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedJobId && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6">
                  Upload #{activeJob?.id || selectedJobId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeJob?.event_name || "Loading..."}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setSelectedJobId(null)}>
                Close
              </Button>
            </DialogTitle>
            <DialogContent dividers>
              {detailState.loading && (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Loading job details...
                </Typography>
              )}
              {detailState.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {detailState.error}
                </Alert>
              )}
              {activeJob && (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {activeJob.status}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Attempts
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {activeJob.attempts}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total Records
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {activeJob.total_record}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Valid / Failed
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {activeJob.valid_record} / {activeJob.failed_record}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {new Date(
                          activeJob.created_datetime
                        ).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Last Processed
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {activeJob.last_processed_datetime
                          ? new Date(
                              activeJob.last_processed_datetime
                            ).toLocaleString()
                          : "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {activeJob.last_error
                      ? `Last error: ${activeJob.last_error}`
                      : "No errors recorded."}
                  </Typography>
                </>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
      <Dialog
        open={Boolean(confirmRerunId)}
        onClose={() => setConfirmRerunId(null)}
      >
        <DialogTitle>Re-run Upload Job</DialogTitle>
        <DialogContent>
          Are you sure you want to re-run job #{confirmRerunId}? This will enqueue the job again.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRerunId(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (confirmRerunId) {
                dispatch(rerunTicketUploadJob(confirmRerunId)).then(() =>
                  dispatch(fetchTicketJobs())
                );
              }
              setConfirmRerunId(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default TicketUploadListPage;
